import { useState, useCallback } from 'react';

export interface UsePaginationProps {
  initialPage?: number;
  initialPageSize?: number;
}

export interface UsePaginationReturn<T> {
  currentPage: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  paginateData: (data: T[]) => T[];
  resetPagination: () => void;
  totalPages: (totalItems: number) => number;
}

/**
 * Hook customizado para gerenciar paginação de dados
 * 
 * @example
 * ```tsx
 * const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination({
 *   initialPage: 1,
 *   initialPageSize: 10
 * });
 * 
 * const paginatedItems = paginateData(items);
 * ```
 */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePagination<T = any>({
  initialPage = 1,
  initialPageSize = 10,
}: UsePaginationProps = {}): UsePaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  /**
   * Pagina um array de dados com base na página atual e tamanho da página
   */
  const paginateData = useCallback(
    (data: T[]): T[] => {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return data.slice(startIndex, endIndex);
    },
    [currentPage, pageSize]
  );

  /**
   * Reseta a paginação para os valores iniciais
   */
  const resetPagination = useCallback(() => {
    setCurrentPage(initialPage);
    setPageSize(initialPageSize);
  }, [initialPage, initialPageSize]);

  /**
   * Calcula o número total de páginas
   */
  const totalPages = useCallback((totalItems: number): number => {
    return Math.ceil(totalItems / pageSize);
  }, [pageSize]);

  /**
   * Handler para mudança de página que reseta para página 1 se mudou o tamanho
   */
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset para primeira página ao mudar tamanho
  }, []);

  return {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize: handlePageSizeChange,
    paginateData,
    resetPagination,
    totalPages,
  };
}
